/**
 * Attachment API hooks。
 * 兩階段上傳:
 *   1) POST /(supplier-)interviews/:id/attachments/presigned-upload → {uploadUrl, storedName, storagePath}
 *   2) PUT uploadUrl body=file (direct to MinIO via Caddy /s3/* 反代)
 *   3) POST /(supplier-)interviews/:id/attachments(寫 DB row)
 *
 * `ownerType='customer'` 走 /interviews/* 與 /attachments/*(預設,維持向後相容)
 * `ownerType='supplier'` 走 /supplier-interviews/* 與 /supplier-attachments/*
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type {
  ApiSuccess,
  CreateAttachmentInput,
  CustomerAttachmentsResponse,
  InterviewAttachment,
  PresignUploadInput,
  PresignUploadResponse,
  AttachmentRotation,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';
import type { InterviewOwnerType } from '../interviews/api';

/** 集合層級 URL 前綴(訪談 nested under) */
function interviewCollection(t: InterviewOwnerType): string {
  return t === 'customer' ? 'interviews' : 'supplier-interviews';
}

/** 附件單筆 URL 前綴(download/delete) */
function attachmentSingle(t: InterviewOwnerType): string {
  return t === 'customer' ? 'attachments' : 'supplier-attachments';
}

/**
 * 依副檔名推 MIME(REQ-20260421003)。
 * 某些作業系統/瀏覽器對 .txt 等常見檔案會給空 type,
 * 若不推測會 fallback 到 application/octet-stream → 觸發後端 ALLOWED_MIME 400。
 * 後端仍會以自己的白名單做最終驗證。
 */
const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  csv: 'text/csv',
  txt: 'text/plain',
  zip: 'application/zip',
};

function guessMime(file: File): string {
  const browserType = file.type;
  if (browserType && browserType !== 'application/octet-stream') return browserType;
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  return MIME_BY_EXT[ext] ?? browserType ?? 'application/octet-stream';
}

/** 完整上傳一個檔案(含 3 個階段),onProgress 0–100。 */
export async function uploadAttachment(
  interviewId: number,
  file: File,
  onProgress?: (pct: number) => void,
  ownerType: InterviewOwnerType = 'customer',
): Promise<InterviewAttachment> {
  const presignBody: PresignUploadInput = {
    originalName: file.name,
    mimeType: guessMime(file),
    sizeBytes: file.size,
  };
  const collection = interviewCollection(ownerType);
  const presigned = await apiClient.post<ApiSuccess<PresignUploadResponse>>(
    `/${collection}/${interviewId}/attachments/presigned-upload`,
    presignBody,
  );
  const { uploadUrl, storedName, storagePath } = presigned.data.data;

  // 直接 PUT 到 MinIO,不經 apiClient(避免 interceptor 自動加 Authorization)
  await axios.put(uploadUrl, file, {
    headers: { 'Content-Type': presignBody.mimeType },
    onUploadProgress: (e) => {
      if (onProgress && e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    },
  });

  const createBody: CreateAttachmentInput = {
    ...presignBody,
    storedName,
    storagePath,
  };
  const created = await apiClient.post<ApiSuccess<InterviewAttachment>>(
    `/${collection}/${interviewId}/attachments`,
    createBody,
  );
  return created.data.data;
}

export function useDeleteAttachment(
  ownerId: number,
  ownerType: InterviewOwnerType = 'customer',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (uuid: string): Promise<void> => {
      await apiClient.delete(`/${attachmentSingle(ownerType)}/${uuid}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['interviews', ownerType, ownerId],
        exact: false,
      });
    },
  });
}

/**
 * 回存附件的檢視旋轉角度(REQ-20260909004)。
 * ⚠️ 只寫 DB 的 `rotation` 欄位 —— MinIO 上的原圖與它的 EXIF 完全不動。
 */
export async function saveAttachmentRotation(
  uuid: string,
  rotation: AttachmentRotation,
  ownerType: InterviewOwnerType = 'customer',
): Promise<InterviewAttachment> {
  const res = await apiClient.patch<ApiSuccess<InterviewAttachment>>(
    `/${attachmentSingle(ownerType)}/${uuid}/rotation`,
    { rotation },
  );
  return res.data.data;
}

/**
 * 附件列表用的回存 mutation —— 成功後讓該 owner 的訪談查詢重取,
 * 新的 `rotation` 才會回流到 lightbox 的「已存角度」。
 * invalidate 的 key 與既有的 `useDeleteAttachment` 同一組。
 */
export function useSaveAttachmentRotation(
  ownerId: number,
  ownerType: InterviewOwnerType = 'customer',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      uuid: string;
      rotation: AttachmentRotation;
    }): Promise<InterviewAttachment> =>
      saveAttachmentRotation(vars.uuid, vars.rotation, ownerType),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ['interviews', ownerType, ownerId],
        exact: false,
      });
    },
  });
}

/** 下載:先呼叫 API(帶 Bearer)拿 signed URL,然後 window.location 跳轉。 */
export async function downloadAttachment(
  uuid: string,
  ownerType: InterviewOwnerType = 'customer',
): Promise<void> {
  const url = await fetchDownloadUrl(uuid, ownerType);
  window.location.href = url;
}

/** 取 signed URL(不觸發下載),供 Lightbox 等就地瀏覽情境使用。 */
export async function fetchDownloadUrl(
  uuid: string,
  ownerType: InterviewOwnerType = 'customer',
): Promise<string> {
  const res = await apiClient.get<ApiSuccess<{ url: string; expiresInSec: number }>>(
    `/${attachmentSingle(ownerType)}/${uuid}/download`,
  );
  return res.data.data.url;
}

/**
 * 附件的 signed URL(REQ-20260806003)—— 就地顯示圖片(稿樣縮圖 / 列印訂單)用。
 * signed URL 5 分鐘過期,staleTime 抓 4 分鐘讓它在過期前重取。
 */
export function useAttachmentUrl(
  uuid: string | undefined,
  ownerType: InterviewOwnerType = 'customer',
) {
  return useQuery({
    queryKey: ['attachment-url', ownerType, uuid],
    enabled: uuid != null,
    queryFn: async (): Promise<string> => fetchDownloadUrl(uuid as string, ownerType),
    staleTime: 4 * 60 * 1000,
  });
}

/**
 * REQ-20260421007 — 某客戶所有圖片附件(圖片牆用)。
 * `enabled` 為 false 時不拉資料,用於 Modal 關閉狀態下避免預載。
 * 目前只支援 customer 模式,supplier 圖片牆暫未實作 / 路由也未開。
 */
export function useCustomerImageAttachments(
  customerId: number | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['customer-image-attachments', customerId],
    enabled: customerId != null && enabled,
    queryFn: async (): Promise<CustomerAttachmentsResponse> => {
      const res = await apiClient.get<CustomerAttachmentsResponse>(
        `/customers/${customerId}/attachments`,
        { params: { kind: 'image' } },
      );
      return res.data;
    },
  });
}
